/**
 * Product Management System - Moon Moon Agency v2.1
 * Shopify product page with variant selection, media updates, and AJAX
 */

/*********************************************************************************************
 * ⚙️ CONFIGURATION & CONSTANTS
 */
const PRODUCT_CONFIG = {
	// API Settings
	debounceDelay: 300,
	apiTimeout: 10000,


	// Selectors
	selectors: {
		variantSelector: 'variant-selector',
		productForm: '#product-form',
		variantInput: '[data-selected-variant-id]',
		productReload: '[data-product-reload]',
		variantFormValue: '[data-variant-value]',
		variantCheckbox: '[data-variant-input]:checked'
	},

	// Development mode (lazy evaluation)
	get isDevelopment() {
		return typeof isDevelopment !== 'undefined' ? isDevelopment : false;
	}
};

/*********************************************************************************************
 * 🛠️ UTILITIES & LOGGING
 */
// Logging system
const productLog = (level, message, data = {}) => {
	if (!PRODUCT_CONFIG.isDevelopment && level === 'info') return;
	if (typeof console !== 'undefined') {
		const logFn = console[level] || console.log;
		logFn(`🛍️ [PRODUCT] ${message}`, { timestamp: window.getTimestamp?.() || new Date().toLocaleString(), ...data });
	}
};

// Product Event Emitter
const productEmit = (event, detail = {}, cancelable = false) => {
	const eventData = {
		timestamp: window.getTimestamp?.() || new Date().toLocaleString(),
		event,
		data: detail,
		source: 'product'
	};

	return document.dispatchEvent(new CustomEvent(`product:${event}`, {
		detail: eventData,
		cancelable,
		bubbles: true
	}));
};

// DOM Selectors Cache for global elements
const ProductDOM = {
	get stickyCTA() { return document.querySelector('[data-sticky-cta]'); },
	get classicCTA() { return document.querySelector('[data-cta]'); },
	get priceElements() { return document.querySelectorAll('[data-price]'); }
};

// Product Data Management
const ProductManager = {
	updateVariantId(container, variantId) {
		if (!variantId) return;

		const variantInput = container?.querySelector(PRODUCT_CONFIG.selectors.variantInput);
		if (variantInput && variantInput.value !== variantId.toString()) {
			const oldValue = variantInput.value;
			variantInput.value = variantId;
			variantInput.dispatchEvent(new Event('change', { bubbles: true }));
			productLog('info', 'Variant ID updated', {
				oldVariantId: oldValue,
				newVariantId: variantId,
				inputElement: variantInput.id || 'no-id',
				containerElement: container?.id || 'no-id'
			});
			productEmit('variant-input-updated', { variantId });
		}
	},

	updateReloadElements(html) {
		const elementsToUpdate = document.querySelectorAll(PRODUCT_CONFIG.selectors.productReload);
		const newElements = html.querySelectorAll(PRODUCT_CONFIG.selectors.productReload);

		productEmit('product-reload-start', { elementsCount: elementsToUpdate.length });

		let updatedCount = 0;

		elementsToUpdate.forEach((currentElement, index) => {
			// Try to match by ID, then data-reload-key, then specific class, fallback to index
			let newElement = null;

			if (currentElement.id) {
				newElement = html.getElementById(currentElement.id);
			} else if (currentElement.dataset.reloadKey) {
				newElement = html.querySelector(`[data-reload-key="${currentElement.dataset.reloadKey}"]`);
			} else if (currentElement.className) {
				// Match by specific component classes for better targeting
				const classList = Array.from(currentElement.classList);
				const specificClass = classList.find(cls =>
					cls.includes('component-price') ||
					cls.includes('component-quantity') ||
					cls.includes('component-cta') ||
					cls.includes('sticky-cta')
				);

				if (specificClass) {
					const candidates = html.querySelectorAll(`.${specificClass}[data-product-reload]`);
					newElement = candidates[0];
				}
			}

			// Fallback to index-based matching
			if (!newElement) {
				newElement = newElements[index];
			}

			if (newElement && this.shouldUpdateElement(currentElement, newElement)) {
				try {
					const isVoid = ['INPUT', 'BR', 'HR', 'IMG', 'META', 'LINK'].includes(currentElement.tagName);
					if (isVoid) {
						// For void/self-closing elements, sync attributes only
						Array.from(newElement.attributes).forEach(attr => {
							currentElement.setAttribute(attr.name, attr.value);
						});
					} else {
						// Update content in-place — avoids element detachment and index shifting
						currentElement.innerHTML = newElement.innerHTML;
						// Sync non-structural attributes (disabled, aria-*, data-*)
						Array.from(newElement.attributes).forEach(attr => {
							if (!['id', 'class'].includes(attr.name)) {
								currentElement.setAttribute(attr.name, attr.value);
							}
						});
					}
					updatedCount++;
					productEmit('product-reload-element-updated', {
						elementIndex: index,
						matchType: currentElement.id ? 'id' : (currentElement.className.includes('component') ? 'class' : 'index')
					});
				} catch (error) {
					productLog('warn', 'Element update failed', {
						index,
						elementId: currentElement.id || 'no-id',
						elementTag: currentElement.tagName,
						elementClasses: currentElement.className,
						error: error.message
					});
				}
			}
		});

		productLog('info', 'DOM reload completed', {
			totalElements: elementsToUpdate.length,
			updatedElements: updatedCount,
			skippedElements: elementsToUpdate.length - updatedCount,
			elementTypes: [...new Set(Array.from(elementsToUpdate).map(el => el.tagName.toLowerCase()))]
		});

		productEmit('product-reload-complete', {
			totalElements: elementsToUpdate.length,
			updatedElements: updatedCount
		});
	},

	shouldUpdateElement(currentElement, newElement) {
		const currentContent = currentElement.innerHTML?.trim();
		const newContent = newElement.innerHTML?.trim();
		return currentContent !== newContent;
	}
};


/*********************************************************************************************
 * 🔄 VARIANT SELECTION LOGIC
 */
class VariantSelector extends HTMLElement {
	constructor() {
		super();
		this.cached = new Map();
		this.currentVariant = null;
		this.options = [];
		this.variantData = null;

		this.isQuickAdd = this.hasAttribute('data-quick-add');
		this.container = this.closest(PRODUCT_CONFIG.selectors.productForm) || this.closest('form');
		if (!this.container) {
			productLog('error', 'VariantSelector: No product form container found');
			return;
		}

		this.onVariantChange = this.onVariantChange.bind(this);
		this.init();
	}

	init() {
		productEmit('variant-selector-init-start');

		try {
			this.addEventListener("change", this.onVariantChange);
			this.getSelectedOptions();

			this.getSelectedVariant();

			productEmit('variant-selector-init-success');
		} catch (error) {
			productLog('error', 'Failed to initialize VariantSelector', { error: error.message });
		}
	}

	onVariantChange() {
		productEmit('variant-change-start');

		try {
			setTimeout(() => {
				this.getSelectedOptions();
				this.getSelectedVariant();

				if (this.currentVariant) {
					productEmit('variant-found', {
						variantId: this.currentVariant.id,
						available: this.currentVariant.available
					});

					// Orchestrate all updates
					if (!this.isQuickAdd) this.updateURL();
					this.updateProductData();
					if (!this.isQuickAdd) this.updateMediaDisplay();
				} else {
					productLog('warn', 'No matching variant found for options', { options: this.options });
					productEmit('variant-not-found', { options: this.options });
				}
			}, 10);
		} catch (error) {
			productLog('error', 'Error in variant change', { error: error.message });
		}
	}

	/**
	 * Update product data
	 */
	async updateProductData() {
		// Update variant ID immediately
		ProductManager.updateVariantId(this.container, this.currentVariant.id);

		// Sync Skio with subscription-capable variant to keep subscription option visible
		// But use current variant's price for accurate one-time purchase display
		const skioPickerEl = this.container.querySelector('skio-plan-picker');
		if (skioPickerEl) {
			const subscriptionVariant = this.currentVariant.selling_plan_allocations?.length > 0
				? this.currentVariant
				: this.getVariantJSON()?.find(v => v.selling_plan_allocations?.length > 0);

			if (subscriptionVariant) {
				// Preserve user's current selection (one-time vs subscription) before setAttribute
				// triggers Skio's updated() which may force subscription if selectedSellingPlan is truthy
				const userHadSubscription = !!skioPickerEl.selectedSellingPlan;
	
				// Pass subscription variant with current variant's price
				skioPickerEl.setAttribute('selectedVariant', JSON.stringify({
					...subscriptionVariant,
					price: this.currentVariant.price,
					compare_at_price: this.currentVariant.compare_at_price,
					id: this.currentVariant.id
				}));
				skioPickerEl._mmCurrentVariantId = this.currentVariant.id;
				skioPickerEl._mmSubscriptionVariantId = subscriptionVariant.id;

				// After Skio re-renders, restore user's selection if they had chosen one-time
				if (!userHadSubscription) {
					skioPickerEl.updateComplete?.then(() => {
						skioPickerEl.selectSellingPlanGroup(null);
					});
				}
			}
		}

		// Skip section re-render in quick-add context
		if (this.isQuickAdd) return;

		// Fetch and update product data
		const html = await this.fetchVariantData();
		if (html) {
			ProductManager.updateReloadElements(html);
			this.updateVariantOptions(html);
		}
	}

	/**
	 * Update media display
	 */
	updateMediaDisplay() {
		// Use MediaManager from the separate media module
		if (window.MediaManager?.updateImagesVariant) {
			window.MediaManager.updateImagesVariant(this.currentVariant);
		} else {
			productLog('warn', 'MediaManager or updateImagesVariant method not available', {
				mediaManagerExists: !!window.MediaManager,
				hasUpdateMethod: !!window.MediaManager?.updateImagesVariant
			});
		}
	}

	/**
	 * Fetch variant data
	 */
	async fetchVariantData() {
		if (!this.currentVariant?.id) return null;

		const baseUrl = this.dataset.url || window.location.pathname;
		const sectionId = this.dataset.section;

		if (!sectionId) {
			productLog('warn', 'VariantSelector: No section ID provided');
			return null;
		}

		const variantUrl = `${baseUrl}?variant=${this.currentVariant.id}&section_id=${sectionId}`;

		// Check cache first
		if (this.cached.has(variantUrl)) {
			return this.cached.get(variantUrl);
		}

		try {
			const response = await fetch(variantUrl);
			if (!response.ok) throw new Error(`HTTP ${response.status}`);

			const responseText = await response.text();
			const html = new DOMParser().parseFromString(responseText, 'text/html');

			// Cache the result
			this.cached.set(variantUrl, html);

			productEmit('variant-fetch-success', { variantId: this.currentVariant.id });
			return html;
		} catch (error) {
			productLog('error', 'Failed to fetch variant data', { error: error.message });
			return null;
		}
	}

	/**
	 * Get selected options from variant inputs
	 */
	getSelectedOptions() {
		const checkboxes = this.querySelectorAll(PRODUCT_CONFIG.selectors.variantCheckbox);
		this.options = Array.from(checkboxes, checkbox => checkbox.value).filter(value => value);
	}

	/**
	 * Get the selected variant based on options
	 */
	getSelectedVariant() {
		const variants = this.getVariantJSON();

		if (!variants.length) {
			this.currentVariant = null;
			return;
		}

		this.currentVariant = variants.find(variant => {
			if (!variant.options || !Array.isArray(variant.options)) return false;
			return variant.options.every((option, index) => this.options[index] === option);
		});
	}

	/**
	 * Get variant data from embedded JSON
	 */
	getVariantJSON() {
		if (!this.variantData) {
			const jsonElement = this.querySelector('[type="application/json"]');

			if (!jsonElement) {
				productLog('error', 'VariantSelector: No JSON data element found');
				return this.variantData = [];
			}

			try {
				const jsonText = jsonElement.textContent.trim();
				this.variantData = jsonText ? JSON.parse(jsonText) : [];
			} catch (error) {
				productLog('error', 'Failed to parse variant JSON', { error: error.message });
				this.variantData = [];
			}
		}
		return this.variantData;
	}

	/**
	 * Update URL with selected variant
	 */
	updateURL() {
		if (!this.currentVariant?.id) return;

		try {
			const baseUrl = this.dataset.url || window.location.pathname;
			const url = new URL(baseUrl, window.location.origin);
			url.searchParams.set('variant', this.currentVariant.id);
			window.history.replaceState({}, '', url.toString());
		} catch (error) {
			productLog('error', 'Failed to update URL', { error: error.message });
		}
	}

	/**
	 * Update variant options without breaking event listeners
	 */
	updateVariantOptions(html) {
		const currentOptions = this.querySelectorAll(PRODUCT_CONFIG.selectors.variantFormValue);
		const newOptions = html.querySelectorAll(PRODUCT_CONFIG.selectors.variantFormValue);

		if (!newOptions.length) return;

		currentOptions.forEach((currentOption, index) => {
			const newOption = newOptions[index];
			if (!newOption) return;

			const currentInput = currentOption.querySelector('input');
			const newInput = newOption.querySelector('input');

			if (currentInput && newInput) {
				// Update only disabled state, preserve checked state
				currentInput.disabled = newInput.disabled;

				const currentlyChecked = currentInput.checked;
				currentOption.className = newOption.className;

				if (currentlyChecked !== currentInput.checked) {
					currentInput.checked = currentlyChecked;
				}
			}
		});
	}
}

/*********************************************************************************************
 * 🏷️ UTILITY FUNCTIONS
 */

/**
 * Change quantity value and update prices
 */
function mmChangeQtty(delta, inputId) {
	const input = document.getElementById(inputId);
	if (!input) return;

	const currentValue = parseInt(input.value, 10) || 1;
	const maxValue = parseInt(input.getAttribute('max'), 10) || 9999;
	const minValue = parseInt(input.getAttribute('min'), 10) || 1;

	// Calculate new value with constraints
	const newValue = Math.max(minValue, Math.min(maxValue, currentValue + parseInt(delta, 10)));

	input.value = newValue;
	input.dispatchEvent(new Event('change', { bubbles: true }));

	// Update prices
	mmUpdatePrice(newValue);

	// Update button states
	updateQuantityButtonStates(input);
}

/**
 * Update plus button disabled state based on current quantity
 */
function updateQuantityButtonStates(input) {
	const currentValue = parseInt(input.value, 10) || 1;
	const maxValue = parseInt(input.getAttribute('max'), 10) || 9999;

	// Find the container
	const container = input.closest('.mm-qtty-container');
	if (!container) return;

	// Find plus button
	const plusBtn = container.querySelector('[data-qtty-increase]');

	// Update plus button only
	if (plusBtn) {
		if (currentValue >= maxValue) {
			plusBtn.setAttribute('disabled', '');
		} else {
			plusBtn.removeAttribute('disabled');
		}
	}
}

/**
 * Update displayed prices based on quantity
 */
function mmUpdatePrice(qtty) {
	const quantity = parseInt(qtty, 10);
	if (!quantity || quantity < 1) return;

	productEmit('quantity-update-start', { quantity });

	try {
		let updatedElementsCount = 0;

		// Update price elements
		ProductDOM.priceElements.forEach(priceElement => {
			const priceValue = parseFloat(priceElement.dataset.price);
			if (!isNaN(priceValue)) {
				const totalPrice = (priceValue * quantity) / 100;
				const formattedPrice = new Intl.NumberFormat('fr-FR', {
					style: 'currency',
					currency: 'EUR'
				}).format(totalPrice);
				priceElement.textContent = formattedPrice;
				updatedElementsCount++;
			}
		});

		productEmit('quantity-update-success', {
			quantity,
			updatedElements: updatedElementsCount
		});

		productLog('info', 'Price update completed', {
			quantity,
			updatedPriceElements: updatedElementsCount
		});
	} catch (error) {
		productLog('error', 'Failed to update prices', {
			quantity,
			error: error.message
		});
		productEmit('quantity-update-error', {
			quantity,
			error: error.message
		});
	}
}

/*********************************************************************************************
 * 📹 STORY VIDEO (stories format)
 */

const STORY_VIDEO_CLASSES = {
	wrapper: 'mm-story-video-wrapper',
	video: 'mm-story-video',
	playBtn: 'mm-icon-play',
	pauseBtn: 'mm-icon-pause',
	playPauseContainer: 'mm-story-video-bottom-play-pause-btn',
	progressBarContainer: 'mm-story-video-bottom-progress-bar-container',
	progressBarValue: 'mm-story-video-bottom-progress-bar-value',
	remainingTime: 'mm-story-video-bottom-remaining-time',
	story: 'mm-product-story',
	visuallyHidden: 'visually-hidden'
};

/**
 * Get story video DOM elements from a trigger element (any element inside the story)
 * @param {Element} trigger - Clicked element or any element inside .mm-product-story
 * @returns {{ wrapper: Element, video: HTMLVideoElement, playBtn: Element, pauseBtn: Element, progressBarValue: Element, remainingTimeEl: Element }|null}
 */
function getStoryVideoElements(trigger) {
	const story = trigger?.closest(`.${STORY_VIDEO_CLASSES.story}`);
	if (!story) return null;

	const wrapper = story.querySelector(`.${STORY_VIDEO_CLASSES.wrapper}`);
	const video = story.querySelector(`.${STORY_VIDEO_CLASSES.wrapper} video`);
	const playPauseContainer = story.querySelector(`.${STORY_VIDEO_CLASSES.playPauseContainer}`);
	const playBtn = playPauseContainer?.querySelector(`.${STORY_VIDEO_CLASSES.playBtn}`);
	const pauseBtn = playPauseContainer?.querySelector(`.${STORY_VIDEO_CLASSES.pauseBtn}`);
	const progressBarContainer = story.querySelector(`.${STORY_VIDEO_CLASSES.progressBarContainer}`);
	const progressBarValue = story.querySelector(`.${STORY_VIDEO_CLASSES.progressBarValue}`);
	const remainingTimeEl = story.querySelector(`.${STORY_VIDEO_CLASSES.remainingTime}`);

	if (!wrapper || !video) return null;

	return { wrapper, video, playBtn, pauseBtn, progressBarContainer, progressBarValue, remainingTimeEl };
}

/**
 * Format seconds as m:ss (e.g. 65 → "1:05")
 * @param {number} seconds
 * @returns {string}
 */
function formatRemainingTime(seconds) {
	if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
	const m = Math.floor(seconds / 60);
	const s = Math.floor(seconds % 60);
	return `${m}:${String(s).padStart(2, '0')}`;
}

/**
 * Update progress bar and remaining time for a story video
 * @param {{ video: HTMLVideoElement, progressBarValue: Element, remainingTimeEl: Element }} ctx
 */
function updateStoryVideoProgress(ctx) {
	const { video, progressBarValue, remainingTimeEl } = ctx;
	if (!video) return;

	const duration = video.duration;
	const current = video.currentTime;
	if (Number.isFinite(duration) && duration > 0 && progressBarValue) {
		progressBarValue.style.width = `${(current / duration) * 100}%`;
	}
	if (remainingTimeEl) {
		const remaining = Math.max(0, duration - current);
		remainingTimeEl.textContent = `- ${formatRemainingTime(remaining)}`;
	}
}

/**
 * Seek story video to a position (0–1) and update UI
 * @param {{ video: HTMLVideoElement, progressBarValue: Element, remainingTimeEl: Element }} ctx
 * @param {number} percent - Position in timeline, 0 to 1
 */
function seekStoryVideo(ctx, percent) {
	const { video, progressBarValue, remainingTimeEl } = ctx;
	if (!video || !Number.isFinite(percent)) return;
	const duration = video.duration;
	if (!Number.isFinite(duration) || duration <= 0) return;

	const clamped = Math.max(0, Math.min(1, percent));
	video.currentTime = clamped * duration;
	updateStoryVideoProgress(ctx);
}

/**
 * Compute seek percent from a mouse event relative to the progress bar container
 * @param {MouseEvent} e
 * @param {Element} container
 * @returns {number} 0–1
 */
function getStoryVideoSeekPercent(e, container) {
	if (!container) return 0;
	const rect = container.getBoundingClientRect();
	const x = e.clientX - rect.left;
	const width = rect.width;
	if (width <= 0) return 0;
	return Math.max(0, Math.min(1, x / width));
}

/**
 * Open story video modal and start playback
 * @param {Element} [trigger] - Element that triggered the action (e.g. play icon on thumbnail)
 */
function openStoryVideo(trigger) {
	const el = typeof trigger === 'object' && trigger?.nodeType ? trigger : null;
	const ctx = getStoryVideoElements(el || document.querySelector(`.${STORY_VIDEO_CLASSES.wrapper}`)?.closest(`.${STORY_VIDEO_CLASSES.story}`));
	if (!ctx) return;

	const { wrapper, video, playBtn, pauseBtn, progressBarValue, remainingTimeEl } = ctx;

	wrapper.classList.remove(STORY_VIDEO_CLASSES.visuallyHidden);
	toggleStoryVideoPlayPause(playBtn || wrapper);
}

/**
 * Close story video modal and stop playback
 * @param {Element} [trigger] - Element that triggered the action (e.g. close icon)
 */
function closeStoryVideo(trigger) {
	const el = typeof trigger === 'object' && trigger?.nodeType ? trigger : null;
	const ctx = getStoryVideoElements(el);
	if (!ctx) return;

	const { wrapper, video, playBtn, pauseBtn, progressBarValue, remainingTimeEl } = ctx;

	wrapper.classList.add(STORY_VIDEO_CLASSES.visuallyHidden);
	video.pause();

	if (playBtn) playBtn.classList.remove(STORY_VIDEO_CLASSES.visuallyHidden);
	if (pauseBtn) pauseBtn.classList.add(STORY_VIDEO_CLASSES.visuallyHidden);
	if (progressBarValue) progressBarValue.style.width = '0%';
	if (remainingTimeEl) remainingTimeEl.textContent = '- --:--';

	const onTimeUpdate = video._storyVideoTimeUpdate;
	if (onTimeUpdate) {
		video.removeEventListener('timeupdate', onTimeUpdate);
		video.removeEventListener('ended', video._storyVideoOnEnded);
		delete video._storyVideoTimeUpdate;
		delete video._storyVideoOnEnded;
	}
}

/**
 * Toggle play/pause for story video and update icons + progress
 * @param {Element} [trigger] - Element that triggered the action (e.g. play/pause icon or video area)
 */
function toggleStoryVideoPlayPause(trigger) {
	const el = typeof trigger === 'object' && trigger?.nodeType ? trigger : null;
	const ctx = getStoryVideoElements(el);
	if (!ctx) return;

	const { wrapper, video, playBtn, pauseBtn, progressBarValue, remainingTimeEl } = ctx;

	if (video.paused) {
		video.play().catch(() => {});

		if (playBtn) playBtn.classList.add(STORY_VIDEO_CLASSES.visuallyHidden);
		if (pauseBtn) pauseBtn.classList.remove(STORY_VIDEO_CLASSES.visuallyHidden);

		const onTimeUpdate = () => updateStoryVideoProgress(ctx);
		const onEnded = () => {
			video.pause();
			if (playBtn) playBtn.classList.remove(STORY_VIDEO_CLASSES.visuallyHidden);
			if (pauseBtn) pauseBtn.classList.add(STORY_VIDEO_CLASSES.visuallyHidden);
			updateStoryVideoProgress(ctx);
		};

		video._storyVideoTimeUpdate = onTimeUpdate;
		video._storyVideoOnEnded = onEnded;
		video.addEventListener('timeupdate', onTimeUpdate);
		video.addEventListener('ended', onEnded);

		updateStoryVideoProgress(ctx);
	} else {
		video.pause();

		if (playBtn) playBtn.classList.remove(STORY_VIDEO_CLASSES.visuallyHidden);
		if (pauseBtn) pauseBtn.classList.add(STORY_VIDEO_CLASSES.visuallyHidden);
	}
}

/**
 * Toggle mute state of story video
 * @param {Element} [trigger] - Element that triggered the action (e.g. mute icon)
 */
function toggleStoryVideoMute(trigger) {
	const el = typeof trigger === 'object' && trigger?.nodeType ? trigger : null;
	const ctx = getStoryVideoElements(el);
	if (!ctx) return;

	ctx.video.muted = !ctx.video.muted;
}

/*********************************************************************************************
 * 🪟 STICKY PANEL
 */

const StickyPanel = {
	get panel() { return document.getElementById('mm-product-sticky-panel'); },

	open() {
		const panel = this.panel;
		if (!panel) return;
		panel.classList.add('active');
		document.body.style.overflow = 'hidden';
		productEmit('sticky-panel-opened');
	},

	close() {
		const panel = this.panel;
		if (!panel) return;
		panel.classList.remove('active');
		document.body.style.overflow = '';
		productEmit('sticky-panel-closed');
	}
};

/*********************************************************************************************
 * 📋 SUBSCRIPTION INFOS PANEL
 */

const SubscriptionInfosPanel = {
	get panel() { return document.querySelector('[data-subscription-infos]'); },

	open() {
		const panel = this.panel;
		if (!panel) return;
		panel.classList.add('active');
		document.body.style.overflow = 'hidden';
		productEmit('subscription-infos-panel-opened');
	},

	close() {
		const panel = this.panel;
		if (!panel) return;
		panel.classList.remove('active');
		document.body.style.overflow = '';
		productEmit('subscription-infos-panel-closed');
	}
};

/*********************************************************************************************
 * 📦 BUNDLE INFOS PANEL
 */

const BundleInfosPanel = {
	get panel() { return document.querySelector('[data-bundle-infos]'); },
	get contentEl() { return document.querySelector('[data-bundle-infos-content]'); },

	open(handle) {
		const panel = this.panel;
		const contentEl = this.contentEl;
		if (!panel || !handle) return;

		const template = document.querySelector(`[data-bundle-panel-content="${handle}"]`);
		if (!template) {
			productLog('warn', 'BundleInfosPanel: no template found for handle', { handle });
			return;
		}

		if (contentEl) {
			contentEl.innerHTML = '';
			contentEl.appendChild(template.content.cloneNode(true));

			// Re-init accordion triggers scoped to the injected content
			const triggers = contentEl.querySelectorAll('[data-dropdown-trigger]');
			triggers.forEach((trigger) => {
				trigger.addEventListener('click', () => {
					DropdownManager.toggle(trigger, Array.from(triggers));
				});
			});
		}

		panel.classList.add('active');
		document.body.style.overflow = 'hidden';
		productEmit('bundle-infos-panel-opened', { handle });
	},

	close() {
		const panel = this.panel;
		if (!panel) return;
		panel.classList.remove('active');
		document.body.style.overflow = '';
		productEmit('bundle-infos-panel-closed');
	}
};

/**
 * Bidirectional variant sync between main product form and sticky panel form
 */
let _syncingVariants = false;

function initStickyPanelVariantSync() {
	const mainForm = document.querySelector('#product-form');
	const stickyForm = document.querySelector('#product-form-sticky-panel');

	if (!mainForm || !stickyForm) return;

	function syncVariants(sourceForm, targetForm) {
		const checkedInputs = sourceForm.querySelectorAll('[data-variant-input]:checked');
		checkedInputs.forEach(input => {
			const targetInput = targetForm.querySelector(
				`[data-variant-input][name="${CSS.escape(input.name)}"][value="${CSS.escape(input.value)}"]`
			);
			if (targetInput && !targetInput.checked) {
				targetInput.checked = true;
				targetInput.dispatchEvent(new Event('change', { bubbles: true }));
			}
		});
	}

	mainForm.addEventListener('change', (e) => {
		if (_syncingVariants || !e.target.matches('[data-variant-input]')) return;
		_syncingVariants = true;
		syncVariants(mainForm, stickyForm);
		_syncingVariants = false;
	});

	stickyForm.addEventListener('change', (e) => {
		if (_syncingVariants || !e.target.matches('[data-variant-input]')) return;
		_syncingVariants = true;
		syncVariants(stickyForm, mainForm);
		_syncingVariants = false;
	});

	productLog('info', 'Sticky panel variant sync initialized');
}

/*********************************************************************************************
 * 📌 STICKY CTA & REASSURANCE
 */

/**
 * Initialize sticky CTA behavior
 */
let _stickyObserver = null;

function initializeStickyCTA() {
	if (_stickyObserver) {
		_stickyObserver.disconnect();
		_stickyObserver = null;
	}

	const classicCTA = ProductDOM.classicCTA;
	const stickyCTA = ProductDOM.stickyCTA;

	if (!classicCTA || !stickyCTA) {
		productLog('warn', 'StickyCTA: Required elements not found');
		return;
	}

	try {
		_stickyObserver = new IntersectionObserver((entries) => {
			entries.forEach(entry => {
				stickyCTA.classList.toggle('mm-none', entry.isIntersecting);
			});
		}, {
			threshold: 0,
			rootMargin: '0px 0px -10px 0px'
		});

		_stickyObserver.observe(classicCTA);

	} catch (error) {
		productLog('error', 'Failed to initialize sticky CTA', { error: error.message });
	}
}

/*********************************************************************************************
 * 🚀 INITIALIZATION
 */

// Define custom element
if (!customElements.get(PRODUCT_CONFIG.selectors.variantSelector)) {
	customElements.define(PRODUCT_CONFIG.selectors.variantSelector, VariantSelector);
}

// Expose story video functions globally (for inline icon_function if used)
window.openStoryVideo = openStoryVideo;
window.closeStoryVideo = closeStoryVideo;
window.toggleStoryVideoPlayPause = toggleStoryVideoPlayPause;
window.toggleStoryVideoMute = toggleStoryVideoMute;

/*********************************************************************************************
 * 🛒 AJAX ADD-TO-CART — opens Prestige cart drawer
 */

/**
 * Initialize AJAX cart handler on #product-form.
 * Intercepts the submit, calls /cart/add.js, then dispatches cart:change
 * which triggers the Prestige cart drawer to open.
 */
function initAjaxCart() {
	const form = document.getElementById('product-form');
	if (!form) return;

	form.addEventListener('submit', async (e) => {
		const submitter = e.submitter;

		// Let express-checkout / buy-now buttons go through normally
		if (submitter?.formAction && !submitter.formAction.includes('/cart/add')) {
			return;
		}

		e.preventDefault();

		const submitBtn = submitter || form.querySelector('button[name="add"]');
		if (submitBtn?.getAttribute('aria-busy') === 'true') return;

		// ── Loading state ──
		if (submitBtn) submitBtn.setAttribute('aria-busy', 'true');
		document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));

		try {
			// Collect section IDs that need to be re-rendered (mirrors Prestige ProductForm)
			const sectionsToBundle = [];
			document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', {
				bubbles: true,
				detail: { sections: sectionsToBundle }
			}));

			// Add to cart — include sections so the response contains rendered drawer HTML
			const formData = new FormData(form);
			formData.set('sections', sectionsToBundle.join(','));
			formData.set('sections_url', `${Shopify.routes.root}variants/${form.querySelector('[data-selected-variant-id]')?.value || ''}`);

			const addRes = await fetch(`${Shopify.routes.root}cart/add.js`, {
				method: 'POST',
				headers: { 'X-Requested-With': 'XMLHttpRequest' },
				body: formData
			});

			if (!addRes.ok) {
				const err = await addRes.json();
				throw new Error(err.description || 'Ajout au panier échoué');
			}

			const addJson = await addRes.json();

			// Get updated cart data then attach sections from add response
			const cart = await (await fetch(`${Shopify.routes.root}cart.js`)).json();
			cart['sections'] = addJson['sections'];

			// Dispatch cart:change — CartDrawer listens on document and will call this.show()
			document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
				bubbles: true,
				detail: {
					baseEvent: 'variant:add',
					onSuccessDo: 'force_open_drawer',
					cart
				}
			}));

			productEmit('cart-add-success', { itemCount: cart.item_count });

		} catch (error) {
			productLog('error', 'Add to cart failed', { error: error.message });
			productEmit('cart-add-error', { error: error.message });
		} finally {
			if (submitBtn) submitBtn.removeAttribute('aria-busy');
			document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
		}
	});
}

const MM_ARROW_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 22 22" fill="none"><path d="M0 10.7072H20M20 10.7072L10 0.707153M20 10.7072L10 20.7072" stroke="black" stroke-width="2"/></svg>`;

function mountSplide(el) {
	if (el.classList.contains('is-initialized')) return;
	const options = el.dataset.splide ? JSON.parse(el.dataset.splide) : {};
	const splide = new Splide(el, options);
	splide.mount();

	if (el.classList.contains('mm-complementary-splide')) {
		el.querySelectorAll('.splide__arrow').forEach(btn => {
			btn.innerHTML = MM_ARROW_SVG;
		});

		const bar = el.querySelector('.splide__progress__bar');
		if (bar) {
			const updateBar = () => {
				const end = splide.Components.Controller.getEnd() + 1;
				bar.style.width = (Math.min((splide.index + 1) / end, 1) * 100) + '%';
			};
			splide.on('move', updateBar);
			splide.on('mounted', updateBar);
			updateBar();
		}
	}
}

function initReassuranceSliders() {
	// Mount sliders already in the DOM (e.g. reassurance bar)
	document.querySelectorAll('.mm-splide').forEach(mountSplide);

	// Watch for async-injected content (e.g. product-recommendations loads via fetch)
	const observer = new MutationObserver(mutations => {
		mutations.forEach(({ addedNodes }) => {
			addedNodes.forEach(node => {
				if (node.nodeType !== Node.ELEMENT_NODE) return;
				// The node itself might be a slider
				if (node.classList?.contains('mm-splide')) mountSplide(node);
				// Or it might contain sliders
				node.querySelectorAll?.('.mm-splide').forEach(mountSplide);
			});
		});
	});

	observer.observe(document.body, { childList: true, subtree: true });
}

// Initialize on DOM ready
document.addEventListener('DOMContentLoaded', () => {
	productEmit('initializing');

	try {
		// Initialize components
		initializeStickyCTA();
		initializeStoryVideoDelegation();
		initStickyPanelVariantSync();
		initAjaxCart();
		initReassuranceSliders();

		productEmit('initialized');
		productLog('info', 'Product page initialized successfully');
	} catch (error) {
		productLog('error', 'Failed to initialize product page', { error: error.message });
	}
});

/**
 * Event delegation for story video actions (clicks on icons/buttons)
 */
function initializeStoryVideoDelegation() {
	document.addEventListener('click', (e) => {
		// Open: click on thumbnail container or play icon overlay
		const thumbnailContainer = e.target.closest('.mm-story-video-thumbnail-container');
		if (thumbnailContainer) {
			openStoryVideo(e.target);
			return;
		}

		// Close: click on close button
		const closeBtn = e.target.closest('.mm-story-video-close');
		if (closeBtn) {
			closeStoryVideo(e.target);
			return;
		}

		// Play/Pause toggle: click on play-pause button area (video area uses inline onclick in Liquid)
		const playPauseBtn = e.target.closest('.mm-story-video-bottom-play-pause-btn');
		if (playPauseBtn) {
			toggleStoryVideoPlayPause(e.target);
			return;
		}

		// Mute toggle: click on mute button area
		const muteBtn = e.target.closest('.mm-story-video-bottom-time-mute-btn');
		if (muteBtn) {
			toggleStoryVideoMute(e.target);
			return;
		}
	});

	// Progress bar: mousedown starts seek + drag (mousemove/mouseup on document)
	document.addEventListener('mousedown', (e) => {
		const progressBarContainer = e.target.closest('.mm-story-video-bottom-progress-bar-container');
		if (!progressBarContainer) return;

		const ctx = getStoryVideoElements(e.target);
		if (!ctx || !ctx.progressBarContainer) return;

		e.preventDefault();
		const percent = getStoryVideoSeekPercent(e, ctx.progressBarContainer);
		seekStoryVideo(ctx, percent);

		const onMove = (moveEvent) => {
			const p = getStoryVideoSeekPercent(moveEvent, ctx.progressBarContainer);
			seekStoryVideo(ctx, p);
		};
		const onUp = () => {
			document.removeEventListener('mousemove', onMove);
			document.removeEventListener('mouseup', onUp);
		};
		document.addEventListener('mousemove', onMove);
		document.addEventListener('mouseup', onUp);
	});
}

/*********************************************************************************************
 * 🎯 ACCESSIBILITY - Quantity Selector Event Listeners (replace inline onclick)
 */

document.addEventListener('DOMContentLoaded', () => {
	// Initialize button states on page load
	const inputs = document.querySelectorAll('input[data-qtty-input]');
	inputs.forEach((input) => {
		updateQuantityButtonStates(input);
	});

	// Use event delegation for quantity buttons (works even after DOM updates)
	// Product page buttons use data-qtty-decrease/increase
	// Cart buttons use data-cart-decrease/increase (handled by mm_cart.js)
	document.addEventListener('click', (e) => {
		// Handle decrease buttons
		const decreaseBtn = e.target.closest('[data-qtty-decrease]');
		if (decreaseBtn) {
			const inputId = decreaseBtn.dataset.qttyDecrease;
			window.mmChangeQtty(-1, inputId);
			return;
		}

		// Handle increase buttons
		const increaseBtn = e.target.closest('[data-qtty-increase]');
		if (increaseBtn) {
			const inputId = increaseBtn.dataset.qttyIncrease;
			window.mmChangeQtty(1, inputId);
			return;
		}
	});

	// Sticky panel: open / close via event delegation
	document.addEventListener('click', (e) => {
		if (e.target.closest('[data-sticky-panel-open]')) {
			StickyPanel.open();
			return;
		}
		if (e.target.closest('[data-sticky-panel-close]')) {
			StickyPanel.close();
		}
	});

	document.addEventListener('keydown', (e) => {
		if (e.key === 'Escape') {
			StickyPanel.close();
			SubscriptionInfosPanel.close();
			BundleInfosPanel.close();
		}
	});

	// Subscription infos panel: open / close via event delegation
	document.addEventListener('click', (e) => {
		if (e.target.closest('[data-subscription-infos-open]')) {
			SubscriptionInfosPanel.open();
			return;
		}
		if (e.target.closest('[data-subscription-infos-close]')) {
			SubscriptionInfosPanel.close();
		}
	});

	// Bundle infos panel: open on product click, close via button or overlay
	document.addEventListener('click', (e) => {
		const bundleProduct = e.target.closest('[data-bundle-product]');
		if (bundleProduct) {
			BundleInfosPanel.open(bundleProduct.dataset.productHandle);
			return;
		}
		if (e.target.closest('[data-bundle-infos-close]')) {
			BundleInfosPanel.close();
		}
	});

	document.addEventListener('cart:opened', () => {
		StickyPanel.close();
	});

	// Show/hide original price when Skio subscription is selected
	document.addEventListener('skio::update-selling-plan', (e) => {
		const isSubscription = !!e.detail.sellingPlan;
		document.querySelectorAll('[data-skio-original-price]').forEach(el => {
			el.classList.toggle('mm-none', !isSubscription);
		});
	});

	// Initialize original price visibility on page load
	// skio::update-selling-plan may fire before this listener is registered (module timing)
	// so we check the Skio component state directly after its first render
	customElements.whenDefined('skio-plan-picker').then(async () => {
		const picker = document.querySelector('skio-plan-picker');
		if (!picker) return;
		await picker.updateComplete;
		const isSubscription = !!picker.selectedSellingPlan;
		document.querySelectorAll('[data-skio-original-price]').forEach(el => {
			el.classList.toggle('mm-none', !isSubscription);
		});
	});

	// Adjust quantity when product reload completes
	document.addEventListener('product:product-reload-complete', () => {
		initializeStickyCTA();
		const inputs = document.querySelectorAll('input[data-qtty-input]');
		inputs.forEach((input) => {
			const currentValue = parseInt(input.value, 10) || 1;
			const maxValue = parseInt(input.getAttribute('max'), 10) || 9999;

			if (currentValue > maxValue) {
				input.value = maxValue;
			}

			// Update button states after variant change
			updateQuantityButtonStates(input);
		});

		// Update price for the first input found (there should be only one on product page)
		if (inputs.length > 0) {
			const newValue = parseInt(inputs[0].value, 10) || 1;
			mmUpdatePrice(newValue);
		}
	});
});

/*********************************************************************************************
 * 🔧 DEVELOPMENT LOGGING
 */
if (PRODUCT_CONFIG.isDevelopment) {
	// Product events
	['initializing', 'initialized', 'variant-change-start', 'variant-found', 'variant-not-found',
		'quantity-update-start', 'quantity-update-success', 'quantity-update-error'].forEach(event => {
		document.addEventListener(`product:${event}`, e =>
			console.log(`🛍️ [PRODUCT] ${event}:`, e.detail)
		);
	});

}
